<template>
  <div id="product-list-form-container">
    <b-row>
      <b-col cols="12">
        <b-form-group label="List name">
          <b-form-input
            id="product-list-name"
            v-model="productListName"
            type="text"
          />
        </b-form-group>
      </b-col>

      <b-col cols="12">
        <div class="d-flex justify-content-end">
          <b-button
            variant="primary"
            @click="handleImportProducts"
          >
            Import Products
          </b-button>

          <b-button
            variant="primary"
            class="ml-2"
            @click="showProductRecordModal"
          >
            Add Product
          </b-button>
        </div>

        <div class="pt-3 mt-3 border-top">
          <b-table
            :items="productRecords"
            :fields="productRecordsFields"
            empty-text="No products added yet"
          >
            <template #cell(product_image)="data">
              <img
                class="w-10 h-8"
                :src="data.item.product_image ? data.item.product_image.url : data.item.productImage.url"
                :alt="data.item.product_name"
              >
            </template>

            <template #cell(upc)="data">
              {{ data.item.upc || '-' }}
            </template>

            <template #cell(brand)="data">
              {{ data.item.brand || '-' }}
            </template>

            <template #cell(variant)="data">
              {{ data.item.variant || '-' }}
            </template>

            <template #cell(primary_color)="data">
              <div
                v-if="data.item.primary_color"
                class="d-flex align-items-center"
              >
                <div
                  class="w-6 h-6 rounded"
                  :style="{ backgroundColor: data.item.primary_color }"
                />

                <span class="ml-2">{{ data.item.primary_color }}</span>
              </div>

              <div v-else>
                -
              </div>
            </template>

            <template #cell(secondary_color)="data">
              <div
                v-if="data.item.secondary_color"
                class="d-flex align-items-center"
              >
                <div
                  class="w-6 h-6 rounded"
                  :style="{ backgroundColor: data.item.secondary_color }"
                />

                <span class="ml-2">{{ data.item.secondary_color }}</span>
              </div>

              <div v-else>
                -
              </div>
            </template>

            <template #cell(actions)="data">
              <b-button
                variant="primary"
                @click="showProductRecordModal(data.item, 'edit')"
              >
                Edit
              </b-button>
              <b-button
                variant="danger"
                @click="handleRemoveProduct(data.item)"
              >
                Remove
              </b-button>
            </template>
          </b-table>
        </div>
      </b-col>

      <b-col
        cols="12"
        class="d-flex justify-content-end"
      >
        <b-button
          variant="secondary"
          @click="handleCancel"
        >
          Cancel
        </b-button>

        <b-button
          variant="primary"
          class="ml-2"
          @click="handleSave"
        >
          Save
        </b-button>
      </b-col>
    </b-row>

    <ProductRecordModal
      ref="productRecordModal"
      @save="handleAddProductRecord"
    />
  </div>
</template>

<script>
import ProductRecordModal from '@frontend/components/modules/product-list/ProductRecordModal.vue';

export default {
  name: 'ProductListForm',
  components: {
    ProductRecordModal,
  },
  props: {
    productList: {
      type: [Object, String],
      required: false,
      default: () => ({}),
    },
  },
  data() {
    return {
      productListName: '',
      productRecords: [],
      productListObject: {},
      productRecordsFields: [
        {
          key: 'product_image',
          label: 'Product Image',
        },
        {
          key: 'product_name',
          label: 'Product Name',
        },
        {
          key: 'gtin',
          label: 'GTIN',
        },
        {
          key: 'upc',
          label: 'UPC',
        },
        {
          key: 'brand',
          label: 'Brand',
        },
        {
          key: 'variant',
          label: 'Variant',
        },
        {
          key: 'primary_color',
          label: 'Primary Color',
        },
        {
          key: 'secondary_color',
          label: 'Secondary Color',
        },
        {
          key: 'actions',
          label: 'Actions',
        },
      ],
      formData: {
        productsToUpdate: [],
        productsToDelete: [],
        productsToAdd: [],
      },
    };
  },
  mounted() {
    if (this.productList) {
      try {
        this.productListObject = JSON.parse(this.productList);
        this.productRecords = this.productListObject.product_list_records || [];
      } catch (error) {
        this.productListObject = {};
      }

      this.productListName = this.productListObject.name || '';
    }
  },
  methods: {
    handleCancel() {
      window.location.href = this.productListObject.id
        ? `/product-list/${this.productListObject.id}`
        : '/product-list/';
    },
    async handleSave() {
      const url = this.productListObject.id
        ? `/product-list/${this.productListObject.id}/edit`
        : '/product-list/create';

      const postData = this.productListObject.id
        ? { name: this.productListName, ...this.formData }
        : { name: this.productListName, productRecords: this.productRecords };

      try {
        const { data } = await axios.post(url, postData);
        window.location.href = `/product-list/${data.productList.id}`;
      } catch (error) {
        console.error('Error saving product list:', error);
        this.$toast.error('Failed to save product list. Please try again.');
      }
    },
    handleImportProducts() {
      toastr.info('Import products feature is not available yet.');
    },
    showProductRecordModal(productRecord = {}, mode = 'create') {
      this.$refs.productRecordModal.show(productRecord, mode);
    },
    handleAddProductRecord({ productData, mode }) {
      if (mode === 'create') {
        productData.id = "new-" + (this.productRecords.length + 1);

        this.productRecords.push(productData);
        this.formData.productsToAdd.push(productData);
        return;
      }

      this.productRecords = this.productRecords.map(product => product.id === productData.id ? productData : product);
      if (typeof productData.id === 'string' && productData.id.startsWith('new-')) {
        this.formData.productsToAdd = this.formData.productsToAdd.map(product => product.id === productData.id ? productData : product);
      } else {
        if (this.formData.productsToUpdate.find(product => product.id === productData.id)) {
          this.formData.productsToUpdate = this.formData.productsToUpdate.map(product => product.id === productData.id ? productData : product);
        } else {
          this.formData.productsToUpdate.push(productData);
        }
      }
    },
    handleRemoveProduct(productRecord) {
      if (typeof productRecord.id === 'string' && productRecord.id.startsWith('new-')) {
        this.formData.productsToAdd = this.formData.productsToAdd.filter(product => product.id !== productRecord.id);
      } else {
        this.formData.productsToDelete.push(productRecord.id);
      }

      this.productRecords = this.productRecords.filter(product => product.id !== productRecord.id);
    },
  },
}
</script>

